import { gql } from '@apollo/client';

// ─── Owner Register ───────────────────────────────────────────────────────────

export const REGISTER_MUTATION = gql`
  mutation Register($input: RegisterInput!) {
    register(input: $input) {
      user {
        id
        businessName
        ownerName
        email
        countryCode
        phone
        isVerified
        isActive
        createdAt
        updatedAt
      }
      accessToken
      refreshToken
    }
  }
`;

export interface RegisterInput {
  businessName: string;
  ownerName: string;
  email: string;
  phone: string;
  countryCode: string;
  password: string;
  confirmPassword: string;
  // Location fields (from Nominatim address search)
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  pincode?: string;
  lat?: number;
  lng?: number;
}

export interface OwnerUser {
  id: string;
  businessName: string;
  ownerName: string;
  profilePic?: string | null;
  email: string;
  countryCode: string;
  phone: string;
  address?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  pincode?: string | null;
  lat?: number | null;
  lng?: number | null;
  isVerified: boolean;
  isActive: boolean;
  stripeConnectAccountId?: string | null;
  stripeOnboardingComplete?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** @deprecated use OwnerUser */
export type AuthUser = OwnerUser;

export interface RegisterResponse {
  register: {
    user: OwnerUser;
    accessToken: string;
    refreshToken: string;
  };
}

// ─── Customer Register ────────────────────────────────────────────────────────

export const REGISTER_CUSTOMER_MUTATION = gql`
  mutation RegisterCustomer($input: CustomerRegisterInput!) {
    registerCustomer(input: $input) {
      customer {
        id
        firstName
        lastName
        profilePic
        email
        countryCode
        phone
        city
        region
        country
        isVerified
        isActive
        createdAt
        updatedAt
      }
      accessToken
      refreshToken
    }
  }
`;

export interface CustomerRegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  phone: string;
  city: string;
  region: string;
  country: string;
  password: string;
  confirmPassword: string;
}

export interface CustomerRegisterResponse {
  registerCustomer: {
    customer: CustomerUser;
    accessToken: string;
    refreshToken: string;
  };
}

// ─── Customer ─────────────────────────────────────────────────────────────────

export interface CustomerUser {
  id: string;
  firstName: string;
  lastName: string;
  profilePic?: string | null;
  email: string;
  countryCode: string;
  phone: string;
  city: string;
  region: string;
  country: string;
  isVerified: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─── Upload Profile Image ─────────────────────────────────────────────────────

export const UPLOAD_PROFILE_IMAGE_MUTATION = gql`
  mutation UploadProfileImage($input: UploadProfileImageInput!) {
    uploadProfileImage(input: $input) {
      url
      message
    }
  }
`;

export interface UploadProfileImageInput {
  imageData: string;
  userType: string;
}

export interface UploadProfileImageResponse {
  uploadProfileImage: {
    url: string;
    message: string;
  };
}

// ─── Unified Login (Owner + Employee + Customer) ──────────────────────────────

export const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      userType
      owner {
        id
        businessName
        ownerName
        profilePic
        email
        countryCode
        phone
        address
        city
        region
        country
        pincode
        lat
        lng
        isVerified
        isActive
        stripeConnectAccountId
        stripeOnboardingComplete
        createdAt
        updatedAt
      }
      employee {
        id
        ownerId
        firstName
        lastName
        profilePic
        dateOfBirth
        gender
        email
        countryCode
        phone
        hireDate
        yearsOfExperience
        bio
        status
        languages
        specialties
        isVerified
        isActive
        isPasswordTemporary
        isVirtualOwner
        createdAt
        updatedAt
      }
      customer {
        id
        firstName
        lastName
        profilePic
        email
        countryCode
        phone
        city
        region
        country
        isVerified
        isActive
        createdAt
        updatedAt
      }
      accessToken
      refreshToken
      isPasswordTemporary
    }
  }
`;

export type UserType = 'owner' | 'employee' | 'customer';

export interface EmployeeUser {
  id: string;
  ownerId: string;
  firstName: string;
  lastName: string;
  profilePic?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  email: string;
  countryCode: string;
  phone: string;
  hireDate?: string | null;
  yearsOfExperience?: number | null;
  bio?: string | null;
  status: string;
  businessName?: string | null;
  languages: string[];
  specialties: string[];
  isVerified: boolean;
  isActive: boolean;
  isPasswordTemporary: boolean;
  isVirtualOwner?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginResponse {
  login: {
    userType: UserType;
    owner: OwnerUser | null;
    employee: EmployeeUser | null;
    customer: CustomerUser | null;
    accessToken: string;
    refreshToken: string;
    isPasswordTemporary: boolean;
  };
}

// ─── Password Reset — OTP Flow ────────────────────────────────────────────────

export const REQUEST_PASSWORD_RESET_MUTATION = gql`
  mutation RequestPasswordReset($input: RequestPasswordResetInput!) {
    requestPasswordReset(input: $input) {
      message
    }
  }
`;

export interface RequestPasswordResetInput {
  email: string;
}

export interface RequestPasswordResetResponse {
  requestPasswordReset: { message: string };
}

export const VERIFY_OTP_MUTATION = gql`
  mutation VerifyOTP($input: VerifyOTPInput!) {
    verifyOTP(input: $input) {
      valid
      message
    }
  }
`;

export interface VerifyOTPInput {
  email: string;
  otp: string;
}

export interface VerifyOTPResponse {
  verifyOTP: { valid: boolean; message: string };
}

export const RESET_PASSWORD_WITH_OTP_MUTATION = gql`
  mutation ResetPasswordWithOTP($input: ResetPasswordWithOTPInput!) {
    resetPasswordWithOTP(input: $input) {
      message
    }
  }
`;

export interface ResetPasswordWithOTPInput {
  email: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ResetPasswordWithOTPResponse {
  resetPasswordWithOTP: { message: string };
}

// ─── Employee Onboarding ─────────────────────────────────────────────────────

export const ONBOARD_EMPLOYEE_MUTATION = gql`
  mutation OnboardEmployee($input: OnboardEmployeeInput!) {
    onboardEmployee(input: $input) {
      employee {
        id
        ownerId
        firstName
        lastName
        email
        countryCode
        phone
        status
        isPasswordTemporary
        createdAt
      }
      message
      invitationSent
    }
  }
`;

export interface EmployeeServiceInput {
  serviceId: string;
  price: number; // onboardEmployee expects Float! (same as updateEmployee)
  duration: number; // onboardEmployee expects Int! (same as updateEmployee)
}

export interface AvailabilityInput {
  day: string;
  available: boolean;
  startTime: string;
  endTime: string;
}

export interface OnboardEmployeeInput {
  firstName: string;
  lastName: string;
  profilePic?: string | null;
  email: string;
  countryCode: string;
  phone: string;
  dateOfBirth?: string | null;
  hireDate?: string | null;
  gender?: string | null;
  yearsOfExperience?: string | null;
  bio?: string | null;
  status: 'active' | 'inactive';
  languageIds: string[];
  specialtyIds: string[];
  services: EmployeeServiceInput[];
  availability: AvailabilityInput[];
  generatedPassword: string;
}

export interface OnboardEmployeeResponse {
  onboardEmployee: {
    employee: {
      id: string;
      ownerId: string;
      firstName: string;
      lastName: string;
      email: string;
      countryCode: string;
      phone: string;
      status: string;
      isPasswordTemporary: boolean;
      createdAt: string;
    };
    message: string;
    invitationSent: boolean;
  };
}

// ─── Me / Logout / RefreshToken ───────────────────────────────────────────────

export const ME_QUERY = gql`
  query Me {
    me {
      userType
      owner {
        id
        businessName
        ownerName
        profilePic
        email
        countryCode
        phone
        address
        city
        region
        country
        pincode
        lat
        lng
        isVerified
        isActive
        stripeConnectAccountId
        stripeOnboardingComplete
        createdAt
        updatedAt
      }
      employee {
        id
        ownerId
        firstName
        lastName
        profilePic
        email
        countryCode
        phone
        status
        isActive
        businessName
        isPasswordTemporary
        isVirtualOwner
        createdAt
        updatedAt
      }
      customer {
        id
        firstName
        lastName
        profilePic
        email
        countryCode
        phone
        city
        region
        country
        isVerified
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

export interface MeResponse {
  me: {
    userType: UserType;
    owner: OwnerUser | null;
    employee: EmployeeUser | null;
    customer: CustomerUser | null;
  };
}

export const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout {
      message
    }
  }
`;

export interface LogoutResponse {
  logout: { message: string };
}

// ─── Update Customer Profile ──────────────────────────────────────────────────

export const UPDATE_MY_CUSTOMER_PROFILE_MUTATION = gql`
  mutation UpdateMyCustomerProfile($input: UpdateMyCustomerProfileInput!) {
    updateMyCustomerProfile(input: $input) {
      id
      firstName
      lastName
      profilePic
      email
      countryCode
      phone
      city
      region
      country
      isVerified
      isActive
      createdAt
      updatedAt
    }
  }
`;

export interface UpdateMyCustomerProfileInput {
  firstName?: string;
  lastName?: string;
  profilePic?: string | null;
  countryCode?: string;
  phone?: string;
  city?: string;
  region?: string;
  country?: string;
}

export interface UpdateMyCustomerProfileResponse {
  updateMyCustomerProfile: CustomerUser;
}

export const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
    }
  }
`;

export interface RefreshTokenResponse {
  refreshToken: { accessToken: string };
}
